/**
 * Angle-based edge attachment utilities.
 *
 * Convention (matches the reference document):
 *   0°  = right,  90° = down,  180° = left,  270° = up
 *
 * A connection endpoint is stored as a single float (degrees). On render
 * we shoot a ray from the node's centre at that angle and compute where it
 * intersects the rectangle's border — that is the attachment point.
 */
import { Position } from "@xyflow/react";

/** Axis-aligned rectangle with top-left origin. */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
  /**
   * Optional border-radius (CSS px). When set, both `pointFromAngle` and
   * `nearestBorderAngle` treat the rectangle as a rounded rectangle — the
   * exit point on a corner runs along the quarter-circle arc instead of the
   * sharp rectangular corner. Lets the orange attachment circle hug the
   * visible rounded shape of ArchiMate types like Bedrijfsdienst /
   * Applicatieservice (border-radius 14) instead of drifting outside it.
   */
  radius?: number;
}

/** Clamp the border-radius to half the smaller side, so a "pill" rectangle
 *  doesn't overflow into negative-length straight segments. */
function effectiveRadius(rect: Rect): number {
  const r = rect.radius ?? 0;
  if (r <= 0) return 0;
  return Math.min(r, rect.w / 2, rect.h / 2);
}

/**
 * Compute the point on the rectangle's border that a ray from the centre
 * at `angleDeg` degrees intersects. When `rect.radius` is set, corners are
 * treated as quarter-circle arcs — the ray exit on a corner uses the arc
 * intersection rather than the sharp bounding-rect corner.
 */
export function pointFromAngle(rect: Rect, angleDeg: number): { x: number; y: number } {
  const cx = rect.x + rect.w / 2;
  const cy = rect.y + rect.h / 2;
  const rad = (angleDeg * Math.PI) / 180;
  const dx = Math.cos(rad);
  const dy = Math.sin(rad);
  // Step 1: sharp-rect exit.
  const sx = Math.abs(dx) > 1e-9 ? rect.w / 2 / Math.abs(dx) : Number.POSITIVE_INFINITY;
  const sy = Math.abs(dy) > 1e-9 ? rect.h / 2 / Math.abs(dy) : Number.POSITIVE_INFINITY;
  const s = Math.min(sx, sy);
  const sharp = { x: cx + dx * s, y: cy + dy * s };
  const r = effectiveRadius(rect);
  if (r === 0) return sharp;
  // Step 2: if the sharp exit lies in a corner zone (within `r` of a
  // bounding-rect corner), the rounded-rect exit is on the corner arc
  // instead. Pick the inset arc-centre and intersect ray with that circle.
  const inLeft = sharp.x < rect.x + r;
  const inRight = sharp.x > rect.x + rect.w - r;
  const inTop = sharp.y < rect.y + r;
  const inBottom = sharp.y > rect.y + rect.h - r;
  if ((inLeft || inRight) && (inTop || inBottom)) {
    const ax = inLeft ? rect.x + r : rect.x + rect.w - r;
    const ay = inTop ? rect.y + r : rect.y + rect.h - r;
    const A = cx - ax;
    const B = cy - ay;
    const b = A * dx + B * dy;
    const c = A * A + B * B - r * r;
    const disc = b * b - c;
    if (disc >= 0) {
      // Larger root = where the ray exits the corner circle on the far
      // (rounded-corner) side, i.e. the rounded-rect border crossing.
      const t = -b + Math.sqrt(disc);
      return { x: cx + dx * t, y: cy + dy * t };
    }
  }
  return sharp;
}

/**
 * Compute the angle (degrees, 0–360) from a centre point to an arbitrary
 * point. Used to determine where on a node edge the mouse landed.
 */
export function angleFromPoint(
  center: { x: number; y: number },
  point: { x: number; y: number },
): number {
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  return ((Math.atan2(dy, dx) * 180) / Math.PI + 360) % 360;
}

/**
 * Given an arbitrary point (px, py) — e.g. the mouse cursor — find the
 * nearest point on the rectangle's perimeter and return the angle from the
 * rectangle's centre to that border point.
 *
 * This is used during attachment-handle dragging so the circle slides 1-to-1
 * with the cursor along the node's edge instead of jumping to the angular
 * projection from the centre (which feels imprecise near corners).
 */
export function nearestBorderAngle(rect: Rect, px: number, py: number): number {
  const cx = rect.x + rect.w / 2;
  const cy = rect.y + rect.h / 2;
  const r = effectiveRadius(rect);
  const candidates: Array<{ x: number; y: number }> = [];
  // Straight edge segments (shortened by `r` on each end when rounded).
  const tlX = rect.x + r;
  const trX = rect.x + rect.w - r;
  const tlY = rect.y + r;
  const blY = rect.y + rect.h - r;
  candidates.push({ x: clamp(px, tlX, trX), y: rect.y }); // top
  candidates.push({ x: rect.x + rect.w, y: clamp(py, tlY, blY) }); // right
  candidates.push({ x: clamp(px, tlX, trX), y: rect.y + rect.h }); // bottom
  candidates.push({ x: rect.x, y: clamp(py, tlY, blY) }); // left
  if (r > 0) {
    // Four corner arcs. Each is a quarter-circle of radius `r`; project
    // the cursor onto the circle and clamp to the arc's angular range.
    const arcs = [
      { ax: rect.x + r, ay: rect.y + r, qStart: 180, qEnd: 270 }, // TL
      { ax: rect.x + rect.w - r, ay: rect.y + r, qStart: 270, qEnd: 360 }, // TR
      { ax: rect.x + rect.w - r, ay: rect.y + rect.h - r, qStart: 0, qEnd: 90 }, // BR
      { ax: rect.x + r, ay: rect.y + rect.h - r, qStart: 90, qEnd: 180 }, // BL
    ];
    for (const a of arcs) {
      const vx = px - a.ax;
      const vy = py - a.ay;
      const d = Math.hypot(vx, vy);
      if (d < 1e-9) {
        const mid = (((a.qStart + a.qEnd) / 2) * Math.PI) / 180;
        candidates.push({ x: a.ax + r * Math.cos(mid), y: a.ay + r * Math.sin(mid) });
        continue;
      }
      const ang = ((Math.atan2(vy, vx) * 180) / Math.PI + 360) % 360;
      const inRange =
        a.qEnd > a.qStart ? ang >= a.qStart && ang <= a.qEnd : ang >= a.qStart || ang <= a.qEnd;
      if (inRange) {
        candidates.push({ x: a.ax + (vx / d) * r, y: a.ay + (vy / d) * r });
      } else {
        // Outside the arc's angular range — closest point on the arc is one
        // of its two endpoints.
        const sRad = (a.qStart * Math.PI) / 180;
        const eRad = (a.qEnd * Math.PI) / 180;
        const p1 = { x: a.ax + r * Math.cos(sRad), y: a.ay + r * Math.sin(sRad) };
        const p2 = { x: a.ax + r * Math.cos(eRad), y: a.ay + r * Math.sin(eRad) };
        const d1 = (p1.x - px) ** 2 + (p1.y - py) ** 2;
        const d2 = (p2.x - px) ** 2 + (p2.y - py) ** 2;
        candidates.push(d1 < d2 ? p1 : p2);
      }
    }
  }
  let best = candidates[0]!;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const c of candidates) {
    const d = (c.x - px) ** 2 + (c.y - py) ** 2;
    if (d < bestDist) {
      bestDist = d;
      best = c;
    }
  }
  return angleFromPoint({ x: cx, y: cy }, best);
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Convert a React Flow handle id to the corresponding angle in degrees.
 * Mid-edge handles ("left", "right", "top", "bottom") wijzen recht naar
 * één kant; corner-handles ("top-left", "top-right", "bottom-left",
 * "bottom-right") wijzen diagonaal weg van de node-rand. Diagonalen
 * volgen dezelfde 0°=rechts / 90°=onder-conventie als de cardinale
 * richtingen.
 */
export function handleIdToAngle(handleId: string | null | undefined): number {
  switch (handleId) {
    case "right":
      return 0;
    case "bottom":
      return 90;
    case "left":
      return 180;
    case "top":
      return 270;
    case "bottom-right":
      return 45;
    case "bottom-left":
      return 135;
    case "top-left":
      return 225;
    case "top-right":
      return 315;
    default:
      return 0;
  }
}

/**
 * Map an angle to the nearest React Flow `Position` so that the smoothstep
 * router starts the curve in the right direction.
 */
export function angleToPosition(angleDeg: number): Position {
  const a = ((angleDeg % 360) + 360) % 360;
  if (a >= 315 || a < 45) return Position.Right;
  if (a >= 45 && a < 135) return Position.Bottom;
  if (a >= 135 && a < 225) return Position.Left;
  return Position.Top;
}

/**
 * Reconstruct the node's absolute rectangle from the handle's absolute
 * position (`hx`, `hy`), the handle's side (`pos`), and the node's
 * rendered size.
 *
 * React Flow passes `sourceX/Y` / `targetX/Y` as the handle's absolute flow
 * coordinates. Knowing which side the handle is on lets us back-calculate
 * the node's top-left corner.
 */
export function rectFromHandle(hx: number, hy: number, pos: Position, w: number, h: number): Rect {
  switch (pos) {
    case Position.Right:
      return { x: hx - w, y: hy - h / 2, w, h };
    case Position.Left:
      return { x: hx, y: hy - h / 2, w, h };
    case Position.Top:
      return { x: hx - w / 2, y: hy, w, h };
    case Position.Bottom:
      return { x: hx - w / 2, y: hy - h, w, h };
  }
}
